import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunks
export const fetchPartnerSubscription = createAsyncThunk(
  'subscription/fetchPartnerSubscription',
  async (partnerId, { rejectWithValue }) => {
    try {
      // Simulate API call
      const response = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            partner_id: partnerId,
            current_tier: 'Classic',
            status: 'active',
            billing_cycle: 'monthly',
            next_billing_date: '2024-02-15T00:00:00Z',
            amount: 50,
            auto_renewal: true,
            payment_method: {
              type: 'card',
              last4: '1234',
              brand: 'Visa',
              expiry: '12/25'
            },
            usage: {
              mothers_limit: 200,
              mothers_used: 150,
              storage_limit: '10GB',
              storage_used: '6.5GB',
              api_calls_limit: 10000,
              api_calls_used: 7500
            },
            features: [
              'Advanced analytics dashboard',
              'Priority customer support',
              '5000 transactions/month',
              'Advanced reporting & insights',
              'API access & integrations',
              'Custom branding options',
              'Real-time notifications',
              'Advanced security features'
            ],
            created_at: '2023-01-15T00:00:00Z',
            updated_at: '2024-01-15T10:30:00Z'
          });
        }, 1000);
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateSubscription = createAsyncThunk(
  'subscription/updateSubscription',
  async ({ partnerId, subscriptionData }, { rejectWithValue }) => {
    try {
      // Simulate API call
      const response = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            ...subscriptionData,
            updated_at: new Date().toISOString()
          });
        }, 800);
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscription/cancelSubscription',
  async (partnerId, { rejectWithValue }) => {
    try {
      // Simulate API call
      const response = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            status: 'cancelled',
            cancelled_at: new Date().toISOString()
          });
        }, 600);
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  subscriptionData: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearSubscriptionError: (state) => {
      state.error = null;
    },
    updateSubscriptionData: (state, action) => {
      state.subscriptionData = { ...state.subscriptionData, ...action.payload };
    },
    setSubscriptionData: (state, action) => {
      state.subscriptionData = action.payload;
    },
    clearSubscriptionData: (state) => {
      state.subscriptionData = null;
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Subscription
      .addCase(fetchPartnerSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPartnerSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptionData = action.payload;
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(fetchPartnerSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Subscription
      .addCase(updateSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptionData = { ...state.subscriptionData, ...action.payload };
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Cancel Subscription
      .addCase(cancelSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptionData = { ...state.subscriptionData, ...action.payload };
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearSubscriptionError,
  updateSubscriptionData,
  setSubscriptionData,
  clearSubscriptionData,
} = subscriptionSlice.actions;

// Selectors
export const selectSubscription = (state) => state.subscription;
export const selectSubscriptionData = (state) => state.subscription.subscriptionData;
export const selectSubscriptionLoading = (state) => state.subscription.isLoading;
export const selectSubscriptionError = (state) => state.subscription.error;
export const selectCurrentTier = (state) => state.subscription.subscriptionData?.current_tier;
export const selectSubscriptionStatus = (state) => state.subscription.subscriptionData?.status;
export const selectUsageData = (state) => state.subscription.subscriptionData?.usage;
export const selectBillingInfo = (state) => ({
  cycle: state.subscription.subscriptionData?.billing_cycle,
  nextDate: state.subscription.subscriptionData?.next_billing_date,
  amount: state.subscription.subscriptionData?.amount,
  autoRenewal: state.subscription.subscriptionData?.auto_renewal
});

export default subscriptionSlice.reducer;
